import React, { useEffect, useState } from 'react';
import i18n from '../../utilities/i18n';
import { ROUTES } from '../../constants';
import {
  Table,
  Label,
  Button,
  LinkText,
} from '@adc/parallax-component-library';
import DynamicTable, {
  CheckboxFilterSection,
  ColumnConfig,
} from '../table/DynamicTable';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { accountsQueryOptions } from '../../queryOptions';
import { UserStatus as AccountStatus } from 'adc-sirius-module-common';
import { Account } from '../../models';
import { enumToCheckboxOptions } from '../../utilities/helpers';
import { AccountActionButtonGroup } from './accountComponents/AccountActionButtonGroup';
import { CreateAccountModal } from '../modal/CreateAccountModal';
import { useNavigateWithBreadcrumbs } from '../../hooks';
import { useAppSelector } from '../../reducers/hooks';

/**
 * View for displaying user accounts
 * @returns {React.JSX.Element}
 */
export default function Accounts() {
  const navigateWithBreadcrumbs = useNavigateWithBreadcrumbs();

  const accounts = useQuery(accountsQueryOptions());
  const queryClient = useQueryClient();

  const { canInsertUsers } = useAppSelector((state) => state.capabilities);

  const [filteredAccounts, setFilteredAccounts] = useState<Account[]>([]);
  const [checkboxFilters, setCheckboxFilters] = useState<
    CheckboxFilterSection[]
  >([
    {
      id: 'status',
      label: i18n.t('Account.status'),
      items: enumToCheckboxOptions(AccountStatus, 'Account.accountStatus'),
    },
  ]);

  const navigateToAccount = (accountKey: string, accountLabel: string) => {
    navigateWithBreadcrumbs(
      ROUTES.ACCOUNTS.DETAIL.replace(':accountKey', accountKey),
      accountLabel,
      i18n.t('Page_Accounts.title'),
    );
  };

  useEffect(() => {
    let filtered = accounts.data || [];

    checkboxFilters.forEach((filter) => {
      const activeFilterValues = filter.items
        .filter((item) => item.value)
        .map((item) => item.id);

      if (activeFilterValues.length > 0) {
        if (filter.id === 'status') {
          filtered = filtered.filter((account) =>
            activeFilterValues.includes(String(account.status)),
          );
        } else {
          filtered = filtered.filter((account) =>
            activeFilterValues.includes(
              String(account[filter.id as keyof Account]),
            ),
          );
        }
      }
    });

    setFilteredAccounts(filtered);
  }, [checkboxFilters, accounts.data]);

  const getAccountFullName = (account: Account) => {
    return `${account.firstName} ${account.lastName}`;
  };

  const getAccountStatusLabel = (status: AccountStatus) => {
    return i18n.t(
      `Account.accountStatus.${AccountStatus[status].toLowerCase()}`,
    );
  };

  const columnData: ColumnConfig[] = [
    {
      label: i18n.t('Page_Accounts.name'),
      dataKey: 'name',
      getSortValue: (account: Account) => getAccountFullName(account),
    },
    { label: i18n.t('Account.email'), dataKey: 'email' },
    { label: i18n.t('Account.username'), dataKey: 'username' },
    {
      label: i18n.t('Account.status'),
      dataKey: 'status',
      getSortValue: (account: Account) => getAccountStatusLabel(account.status),
    },
    { label: '', dataKey: 'action', isSortable: false },
  ];

  const getStatusLabelType = (status: AccountStatus) => {
    switch (status) {
      case AccountStatus.Invited:
        return 'caution';
      case AccountStatus.NotInvited:
        return 'warning';
      case AccountStatus.Locked:
        return 'danger';
      case AccountStatus.Verified:
        return 'success';
      default:
        return 'generic';
    }
  };

  const cellRenderer = (dataKey: string, account: Account) => {
    if (dataKey === 'name') {
      const name = getAccountFullName(account);

      return (
        <Table.TextCell key={dataKey}>
          <LinkText
            onPress={() => navigateToAccount(account.key, name)}
            type="$bodySmall.default"
            testID={`${account.key}-link`}
          >
            {name}
          </LinkText>
        </Table.TextCell>
      );
    } else if (dataKey === 'status') {
      return (
        <Table.LabelCell
          key={dataKey}
          type={getStatusLabelType(account.status)}
        >
          <Label.Text>{getAccountStatusLabel(account.status)}</Label.Text>
        </Table.LabelCell>
      );
    } else if (dataKey === 'action') {
      return (
        <Table.TextCell key={dataKey}>
          <AccountActionButtonGroup
            account={account}
            onSuccess={() =>
              queryClient.invalidateQueries({
                queryKey: accountsQueryOptions().queryKey,
              })
            }
          />
        </Table.TextCell>
      );
    } else {
      return <Table.TextCell key={dataKey}>{account[dataKey]}</Table.TextCell>;
    }
  };

  return (
    <DynamicTable
      testID="accounts"
      isLoading={accounts.isPending}
      hasLoadingError={accounts.isError}
      title={i18n.t('Page_Accounts.title')}
      description={i18n.t('Page_Accounts.table')}
      columnData={columnData}
      cellRenderer={cellRenderer}
      rowData={filteredAccounts}
      enableFiltering={true}
      checkboxFilters={checkboxFilters}
      onFiltersChange={setCheckboxFilters}
      onFiltersReset={() => {
        setCheckboxFilters((prevFilters) =>
          prevFilters.map((filter) => ({
            ...filter,
            items: filter.items.map((item) => ({ ...item, value: false })),
          })),
        );
      }}
      toolbarRightContent={
        canInsertUsers ? (
          <CreateAccountModal
            trigger={
              <Button
                size="medium"
                label={i18n.t('Page_Accounts.createAccount')}
                aria-label={i18n.t('Page_Accounts.createAccount')}
                onPress={() => {}}
                type="button"
                testID="create-account-button"
              />
            }
          />
        ) : null
      }
    />
  );
}
